import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { useAppLogo } from '../hooks/useAppLogo';
import Icon from '../components/Icon';
import { COMPANY_BASIC_FREE_CAMPAIGN_END_LABEL, isCompanyBasicFreeCampaignActive } from '../utils/campaigns';
import '../styles/LandingPage.css';

// Detailed feature descriptions for each tier
const tierDetails = {
  en: {
    free: {
      title: 'Cyclist - Free Forever',
      subtitle: 'Everything you need to track your bike\'s service history',
      description: 'The Cyclist plan is completely free and gives bike owners full access to track their bike\'s service history, manage documents, and find trusted workshops.',
      features: [
        { name: 'Unlimited Bike Registration', description: 'Register as many bikes as you own - no limits' },
        { name: 'Complete Service History', description: 'View all services performed on your bikes by partner workshops' },
        { name: 'Digital Bike Passport', description: 'Your bike\'s complete digital identity - frame number, specs, photos' },
        { name: 'Document Storage', description: 'Store receipts, warranties, and purchase documents' },
        { name: 'Maintenance Reminders', description: 'Get notified when annual service is due' },
        { name: 'Find Workshops', description: 'Search for trusted workshops on the map' },
        { name: 'Book Appointments', description: 'Book service appointments directly with partner shops' },
        { name: 'Rate & Review', description: 'Leave reviews for workshops you\'ve visited' },
      ],
    },
    basic: {
      title: 'Basic - For Small Workshops',
      subtitle: 'Manual service management made easy',
      description: 'The Basic plan is perfect for small bike shops that want to provide digital service records to their customers without automation.',
      features: [
        { name: 'Manual Service Logging', description: 'Record services manually with full details, parts, and pricing' },
        { name: 'Manual Bike Registration', description: 'Add customer bikes to the system manually' },
        { name: 'Customer Management', description: 'Manage customer profiles and their bikes' },
        { name: 'Digital Bike Passport', description: 'Create digital passports for customer bikes' },
        { name: 'Service Receipts', description: 'Generate and store digital service receipts' },
        { name: 'Public Map Listing', description: 'Appear on the public shop finder map' },
        { name: 'Customer Reviews', description: 'Collect and display customer ratings and reviews' },
        { name: 'Basic Dashboard', description: 'Overview of shop activity and key metrics' },
      ],
    },
    professional: {
      title: 'Professional - AI Receipt Extraction',
      subtitle: 'Smart service entry from photo receipts',
      description: 'The Professional plan lets you photograph receipts and invoices. AI extracts bike frame number, service items, parts, and prices automatically. Add services in seconds instead of manual data entry.',
      features: [
        { name: 'All Basic Features', description: 'Everything in Basic, plus AI tools' },
        { name: 'AI item extraction from photo receipt', description: 'Take a photo of any receipt or invoice – AI extracts service items, parts, prices, and bike info automatically', highlight: true },
        { name: 'Frame Number Matching', description: 'Automatically matches services to bikes by frame number' },
      ],
    },
    enterprise: {
      title: 'Enterprise - Full Business Suite',
      subtitle: 'Billing integrations + complete workshop management',
      description: 'The Enterprise plan connects Frame-Book directly to your billing system. Services automatically sync to Square, Stripe, Xero, or other platforms. Plus online booking, team management, and marketing tools.',
      features: [
        { name: 'All Professional Features', description: 'Everything in Professional, plus integrations' },
        { name: 'Billing Integration', description: 'Connect to Square, Stripe, Xero, QuickBooks, CitrusLime, Lightspeed' },
        { name: 'Auto Invoice Sync', description: 'Services automatically create invoices in your billing system' },
        { name: 'Online Booking System', description: 'Let customers book appointments 24/7 from your website' },
        { name: 'Smart Technician Assignment', description: 'AI-powered automatic assignment of bookings to the best available technician based on skills and workload', highlight: true },
        { name: 'Smart Work-Off Delegation', description: 'Intelligent work redistribution when technicians take time off - automatically reassigns tasks to available team members', highlight: true },
        { name: 'Team Management (50)', description: 'Add technicians with individual logins and schedules' },
        { name: 'Technician Scheduling', description: 'Assign bookings to specific team members' },
        { name: 'Email & SMS Marketing', description: 'Send promotional campaigns to your customers' },
        { name: 'Multi-Location Support', description: 'Manage multiple shop locations from one account' },
        { name: 'Advanced Analytics', description: 'Detailed revenue tracking, trends, and business insights' },
        { name: 'API Access', description: 'Full API access for custom integrations' },
        { name: 'Priority Support', description: 'Dedicated support with faster response times' },
        { name: 'SLA Guarantee', description: 'Service level agreement with uptime guarantees' },
      ],
    },
  },
  hu: {
    free: {
      title: 'Kerékpáros - Örökre Ingyenes',
      subtitle: 'Minden, ami a biciklid szerviz történetének követéséhez kell',
      description: 'A Kerékpáros csomag teljesen ingyenes, és teljes hozzáférést biztosít a biciklid szerviz történetéhez, dokumentumokhoz és megbízható műhelyek kereséséhez.',
      features: [
        { name: 'Korlátlan Bicikli Regisztráció', description: 'Regisztrálj annyi biciklit, amennyid van - korlátlanul' },
        { name: 'Teljes Szerviz Történet', description: 'Lásd az összes elvégzett szervizt a partnermühelyektől' },
        { name: 'Digitális Bicikli Útlevél', description: 'A biciklid teljes digitális azonosítója - vázszám, specifikációk, fotók' },
        { name: 'Dokumentum Tárolás', description: 'Tárold a bizonylatokat, garanciákat és vásárlási dokumentumokat' },
        { name: 'Karbantartási Emlékeztetők', description: 'Kapj értesítést az éves szerviz esedékességéről' },
        { name: 'Műhely Keresés', description: 'Keress megbízható műhelyeket a térképen' },
        { name: 'Időpontfoglalás', description: 'Foglalj szerviz időpontot közvetlenül a partnerműhelyeknél' },
        { name: 'Értékelés & Vélemény', description: 'Írj véleményt a meglátogatott műhelyekről' },
      ],
    },
    basic: {
      title: 'Basic - Kis Műhelyeknek',
      subtitle: 'Egyszerű manuális szervizkezelés',
      description: 'A Basic csomag tökéletes kis bicikliszervizeknek, amelyek digitális szerviznyilvántartást szeretnének ügyfeleiknél automatizálás nélkül.',
      features: [
        { name: 'Manuális Szerviz Rögzítés', description: 'Rögzíts szervizeket minden részlettel, alkatrészekkel és árakkal' },
        { name: 'Manuális Bicikli Felvétel', description: 'Add hozzá az ügyfelek biciklijeit manuálisan' },
        { name: 'Ügyfélkezelés', description: 'Kezeld az ügyfélprofilokat és biciklijeiket' },
        { name: 'Digitális Bicikli Útlevél', description: 'Hozz létre digitális útlevelet az ügyfelek biciklijeihez' },
        { name: 'Szerviz Bizonylatok', description: 'Generálj és tárolj digitális szervizszámlákat' },
        { name: 'Térképes Megjelenés', description: 'Jelenj meg a nyilvános műhelykereső térképen' },
        { name: 'Ügyfélértékelések', description: 'Gyűjts és jeleníts meg ügyfélértékeléseket' },
        { name: 'Alapszintű Irányítópult', description: 'Áttekintés a műhely tevékenységéről' },
      ],
    },
    professional: {
      title: 'Professional - AI Számla Feldolgozás',
      subtitle: 'Okos szerviz felvétel fotó számlákból',
      description: 'A Professional csomaggal fényképezd le a számlákat – az AI automatikusan kinyeri a vázszámot, szerviz tételeket, alkatrészeket és árakat. Másodpercek alatt add hozzá a szervizeket a manuális adatbevitel helyett.',
      features: [
        { name: 'Minden Basic Funkció', description: 'A Basic összes funkciója, plusz AI eszközök' },
        { name: 'AI elemek kinyerése fotó számlából', description: 'Fotózd le bármilyen számlát vagy bizonylatot – az AI automatikusan kinyeri a szerviz tételeket, alkatrészeket, árakat és bicikli adatokat', highlight: true },
        { name: 'Vázszám Párosítás', description: 'Automatikusan párosítja a szervizeket a biciklikhez vázszám alapján' },
      ],
    },
    enterprise: {
      title: 'Enterprise - Teljes Uzleti Csomag',
      subtitle: 'Billing integrations + complete workshop management',
      description: 'Az Enterprise csomag osszekoti a Frame-Bookot a szamlazasi rendszereddel. Plusz online foglalas, csapatkezeles es marketing eszkozok.',
      features: [
        { name: 'Minden Professional Funkcio', description: 'A Professional osszes funkcioja, plusz integraciok' },
        { name: 'Szamlazasi Integracio', description: 'Kapcsolodas Square, Stripe, Xero, QuickBooks, CitrusLime, Lightspeed rendszerekhez' },
        { name: 'Auto Szamla Szinkron', description: 'A szervizek automatikusan szamlakat hoznak letre a szamlazasi rendszerben' },
        { name: 'Online Foglalasi Rendszer', description: 'Az ugyfelek 24/7 foglalhatnak idopontot a weboldaladon' },
        { name: 'Smart Technician Assignment', description: 'AI-powered automatic assignment to the best available technician', highlight: true },
        { name: 'Smart Work-Off Delegation', description: 'Automatic work redistribution when technicians are unavailable', highlight: true },
        { name: 'Csapatkezeles (50 fo)', description: 'Adj hozza szereloket egyeni bejelentkezessel es beosztassal' },
        { name: 'Szerelo Beosztas', description: 'Rendelj foglalasokat specifikus csapattagokhoz' },
        { name: 'E-mail & SMS Marketing', description: 'Kuldj promocios kampanyokat az ugyfeleidnek' },
        { name: 'Tobb Helyszin Tamogatas', description: 'Kezelj tobb muhely helyszint egy fiokbol' },
        { name: 'Halado Analitika', description: 'Reszletes bevetelkovetes, trendek es uzleti betekintesek' },
        { name: 'API Hozzaferes', description: 'Teljes API hozzaferes egyedi integraciokhoz' },
        { name: 'Prioritasos Tamogatas', description: 'Dedikalt tamogatas gyorsabb valaszidovel' },
        { name: 'SLA Garancia', description: 'Szolgaltatasi szint szerzodes rendelkezesre allasi garanciaval' },
      ],
    },
  },
  de: {
    free: {
      title: 'Radfahrer - Für Immer Kostenlos',
      subtitle: 'Alles, was du brauchst, um die Service-Historie deines Fahrrads zu verfolgen',
      description: 'Der Radfahrer-Plan ist völlig kostenlos und gibt Fahrradbesitzern vollen Zugang zur Service-Historie, Dokumenten und vertrauenswürdigen Werkstätten.',
      features: [
        { name: 'Unbegrenzte Fahrrad-Registrierung', description: 'Registriere so viele Fahrräder wie du besitzt - keine Limits' },
        { name: 'Komplette Service-Historie', description: 'Sieh alle Services von Partnerwerkstätten' },
        { name: 'Digitaler Fahrrad-Pass', description: 'Die komplette digitale Identität deines Fahrrads' },
        { name: 'Dokumentenspeicher', description: 'Speichere Belege, Garantien und Kaufdokumente' },
        { name: 'Wartungserinnerungen', description: 'Werde benachrichtigt, wenn der jährliche Service fällig ist' },
        { name: 'Werkstatt-Suche', description: 'Suche vertrauenswürdige Werkstätten auf der Karte' },
        { name: 'Termine Buchen', description: 'Buche Service-Termine direkt bei Partnerwerkstätten' },
        { name: 'Bewerten & Rezensieren', description: 'Hinterlasse Bewertungen für besuchte Werkstätten' },
      ],
    },
    basic: {
      title: 'Basic - Für Kleine Werkstätten',
      subtitle: 'Einfache manuelle Service-Verwaltung',
      description: 'Der Basic-Plan ist perfekt für kleine Fahrradwerkstätten, die digitale Service-Aufzeichnungen ohne Automatisierung anbieten möchten.',
      features: [
        { name: 'Manuelle Service-Erfassung', description: 'Erfasse Services mit allen Details, Teilen und Preisen' },
        { name: 'Manuelle Fahrrad-Registrierung', description: 'Füge Kundenfahrräder manuell hinzu' },
        { name: 'Kundenverwaltung', description: 'Verwalte Kundenprofile und ihre Fahrräder' },
        { name: 'Digitaler Fahrrad-Pass', description: 'Erstelle digitale Pässe für Kundenfahrräder' },
        { name: 'Service-Belege', description: 'Generiere und speichere digitale Service-Belege' },
        { name: 'Kartenlistung', description: 'Erscheine auf der öffentlichen Werkstatt-Karte' },
        { name: 'Kundenbewertungen', description: 'Sammle und zeige Kundenbewertungen' },
        { name: 'Basis-Dashboard', description: 'Überblick über Werkstatt-Aktivitäten' },
      ],
    },
    professional: {
      title: 'Professional - KI Rechnungs-Extraktion',
      subtitle: 'Intelligente Service-Erfassung aus Foto-Rechnungen',
      description: 'Der Professional-Plan ermöglicht das Fotografieren von Rechnungen und Belegen. Die KI extrahiert Rahmennummer, Service-Artikel, Teile und Preise automatisch. Services in Sekunden statt manueller Dateneingabe.',
      features: [
        { name: 'Alle Basic-Funktionen', description: 'Alles aus Basic, plus KI-Tools' },
        { name: 'KI Artikel-Extraktion aus Foto-Rechnung', description: 'Fotografiere jede Rechnung oder Beleg – die KI extrahiert automatisch Service-Artikel, Teile, Preise und Fahrrad-Info', highlight: true },
        { name: 'Rahmennummer-Matching', description: 'Automatische Zuordnung von Services zu Fahrrädern' },
      ],
    },
    enterprise: {
      title: 'Enterprise - Komplettes Business-Paket',
      subtitle: 'Billing integrations + complete workshop management',
      description: 'Der Enterprise-Plan verbindet Frame-Book direkt mit deinem Abrechnungssystem. Plus Online-Buchung, Team-Verwaltung und Marketing-Tools.',
      features: [
        { name: 'Alle Professional-Funktionen', description: 'Alles aus Professional, plus Integrationen' },
        { name: 'Abrechnungs-Integration', description: 'Verbinde Square, Stripe, Xero, QuickBooks, CitrusLime, Lightspeed' },
        { name: 'Auto Rechnungs-Sync', description: 'Services erstellen automatisch Rechnungen im Abrechnungssystem' },
        { name: 'Online-Buchungssystem', description: 'Kunden koennen 24/7 von deiner Website buchen' },
        { name: 'Smart Technician Assignment', description: 'KI-gestuetzte automatische Zuweisung an den besten verfuegbaren Techniker', highlight: true },
        { name: 'Smart Work-Off Delegation', description: 'Automatische Umverteilung von Aufgaben bei Abwesenheit', highlight: true },
        { name: 'Team-Verwaltung (50)', description: 'Fuege Techniker mit eigenen Logins und Zeitplaenen hinzu' },
        { name: 'Techniker-Planung', description: 'Weise Buchungen bestimmten Teammitgliedern zu' },
        { name: 'E-Mail & SMS Marketing', description: 'Sende Werbekampagnen an deine Kunden' },
        { name: 'Multi-Standort-Support', description: 'Verwalte mehrere Werkstatt-Standorte von einem Konto' },
        { name: 'Erweiterte Analytik', description: 'Detaillierte Umsatzverfolgung, Trends und Business-Einblicke' },
        { name: 'API-Zugang', description: 'Voller API-Zugang fuer individuelle Integrationen' },
        { name: 'Prioritaets-Support', description: 'Dedizierter Support mit schnelleren Antwortzeiten' },
        { name: 'SLA-Garantie', description: 'Service-Level-Vereinbarung mit Verfuegbarkeitsgarantien' },
      ],
    },
  },
};

// Language options removed - English only

const translations = {
  hu: {
    nav: {
      features: 'Funkciók',
      forRiders: 'Kerékpárosoknak',
      forShops: 'Szervizeknek',
      pricing: 'Árazás',
      contact: 'Kapcsolat',
    },
    headerAuth: {
      login: 'Bejelentkezés',
      register: 'Regisztráció',
      dashboard: 'Vissza az appba',
      logout: 'Kijelentkezés',
    },
    hero: {
      badge: 'Bicikli Útlevél',
      title: 'A Globális Standard',
      titleHighlight: 'a Bicikliszervizben',
      lead: 'A Frame-Book egy digitális ökoszisztéma, amely összeköti a biciklitulajdonosokat és a szervizeket. Szerezd meg biciklid digitális útlevelét – a Bike Passport-ot – teljesen ingyen.',
      supporting: 'Átláthatóság a kerékpárosoknak. Hitelesség a műhelyeknek.',
      pills: [
        'Biciklid Digitális Szerviz Útlevele',
        'Keress és mutass megbízhatóságot minden munkával',
        'Egységesített szerviz adatok világszerte',
      ],
      ctaPrimary: 'Regisztrálj ingyen',
      ctaSecondary: 'Szerviz vagy? Próbáld ki',
      ctaDemo: 'Próbáld ki most',
      ctaDemoSubtext: 'Regisztráció nélkül',
    },
    stats: {
      bikes: 'Regisztrált bicikli',
      users: 'Elégedett kerékpáros',
      services: 'Elvégzett szerviz',
    },
    introVideo: {
      eyebrow: 'Bemutató',
      title: 'Ismerd meg a Frame-Bookot',
      subtitle: 'Rövid intro Denestől',
    },
    audiences: {
      eyebrow: 'Kinek készült?',
      title: 'Egy platform, két világ',
      riders: {
        badge: 'Ingyen',
        title: 'Kerékpárosoknak',
        subtitle: 'Biciklid Digitális Szerviz Útlevele',
        description: 'Tartsd számon biciklid teljes szerviz történetét – szervizek, alkatrészek, dokumentumok. Kapj értesítést, ha itt az ideje a karbantartásnak.',
        benefits: [
          { icon: '📋', text: 'Teljes bicikli útlevél' },
          { iconName: 'notifications', text: 'Karbantartási emlékeztetők' },
          { icon: '📄', text: 'Digitális bizonylatok' },
          { icon: '🔒', text: 'Tulajdonjog igazolás' },
          { icon: '📸', text: 'Fotók és dokumentumok' },
          { icon: '💬', text: 'Közvetlen kapcsolat szervizzel' },
        ],
        cta: 'Regisztrálj ingyen',
        price: 'Teljesen ingyenes, örökre',
      },
      shops: {
        badge: 'Pro',
        title: 'Műhelyeknek',
        subtitle: 'Keress és mutass megbízhatóságot minden munkával',
        description: 'Professzionális státusz és ügyfélkezelő rendszer. Kezeld ügyfeleidet, foglalásokat, marketinget és számlázást egyetlen felületről. Növeld a lojalitást és csökkentsd az adminisztrációt.',
        benefits: [
          { iconName: 'calendar', text: 'Online foglalási rendszer' },
          { iconName: 'users', text: 'Ügyfél- és bicikli nyilvántartás' },
          { iconName: 'email', text: 'Automatikus emlékeztetők' },
          { iconName: 'analytics', text: 'Üzleti analitika' },
          { icon: '💳', text: 'Számlázás és fizetés' },
          { icon: '📣', text: 'Marketing kampányok' },
        ],
        cta: '14 napos próba indítása',
        price: 'Már £6.99/hó-tól',
      },
    },
    howItWorks: {
      eyebrow: 'Hogyan működik?',
      title: 'Egyszerű, mint kerékpározni',
      steps: [
        {
          number: '01',
          title: 'Regisztrálj',
          description: 'Hozd létre fiókod percek alatt, teljesen ingyen.',
        },
        {
          number: '02',
          title: 'Add hozzá biciklidet',
          description: 'Rögzítsd biciklid adatait, fotóit és dokumentumait.',
        },
        {
          number: '03',
          title: 'Válassz szervizt',
          description: 'Foglalj időpontot a közelben lévő partnerszervizekhez.',
        },
        {
          number: '04',
          title: 'Kövesd az útlevelet',
          description: 'Minden szerviz automatikusan megjelenik a bicikli útlevélben.',
        },
      ],
    },
    features: {
      eyebrow: 'Funkciók',
      title: 'Minden, amire szükséged van',
      groups: [
        {
          iconName: 'bike',
          title: 'Bicikli Profil',
          subtitle: 'Minden adat egy helyen',
          bullets: [
            'Egyedi azonosító minden biciklihez',
            'Fotók, dokumentumok feltöltése',
            'Tulajdonos váltás nyomkövetéssel',
          ],
        },
        {
          iconName: 'service',
          title: 'Szerviz Napló',
          subtitle: 'Teljes előzmény',
          bullets: [
            'Minden szerelés részletesen',
            'Beépített alkatrészek listája',
            'Bizonylatok és számlák',
          ],
        },
        {
          iconName: 'calendar',
          title: 'Foglalás',
          subtitle: 'Online időpontfoglalás',
          bullets: [
            'Valós idejű elérhetőség',
            'Szolgáltatás választó',
            'Automatikus megerősítés',
          ],
        },
        {
          iconName: 'notifications',
          title: 'Emlékeztetők',
          subtitle: 'Ne maradj le semmiről',
          bullets: [
            'Éves karbantartás figyelmeztetés',
            'Garancia lejárat',
            'Szezonális tippek',
          ],
        },
        {
          iconName: 'analytics',
          title: 'Analitika',
          subtitle: 'Szervizeknek',
          bullets: [
            'Ügyfél statisztikák',
            'Bevétel követés',
            'Marketing hatékonyság',
          ],
        },
        {
          iconName: 'integration',
          title: 'Integráció',
          subtitle: 'Kapcsolódj mindenhez',
          bullets: [
            'Google Calendar szinkron',
            'E-mail értesítések',
          ],
        },
      ],
    },
    preview: {
      calendar: {
        title: 'Foglalási naptár',
        description: 'Tekintsd meg a szabad időpontokat és foglalj online.',
      },
      passport: {
        title: 'Bicikli Útlevél',
        description: 'Minden szerviz, alkatrész és dokumentum egy helyen.',
      },
      dashboard: {
        title: 'Irányítópult',
        description: 'Áttekintő nézet a legfontosabb információkról.',
      },
    },
    testimonials: {
      eyebrow: 'Vélemények',
      title: 'Mit mondanak rólunk?',
      items: [
        {
          quote: 'Végre tudom, mikor volt utoljára szervizelve a biciklim. A Frame-Book teljesen megváltoztatta, hogyan gondozom a kerékpáromat.',
          author: 'Kovács Péter',
          role: 'Hobbi kerékpáros',
        },
        {
          quote: 'Az ügyfeleim imádják, hogy minden szerviz automatikusan megjelenik náluk. Nekem pedig rengeteg időt spórol az adminisztráció.',
          author: 'Pedál Mester Kft.',
          role: 'Partnerszerviz',
        },
        {
          quote: 'Használt biciklit vettem és láttam a teljes előzményét. Megérte a pénzét, tudtam, mibe fektetem.',
          author: 'Nagy Anna',
          role: 'Városi kerékpáros',
        },
      ],
    },
    pricing: {
      eyebrow: 'Árazás',
      title: 'Egyszerű és átlátható',
      description: 'Kerékpárosoknak ingyenes. Szervizeknek kedvező havidíjak.',
      plans: [
        {
          name: 'Kerékpáros',
          price: 'Ingyenes',
          period: 'örökre',
          audience: 'Bicikli tulajdonosoknak',
          highlighted: false,
          tier: 'free',
          benefits: [
            'Korlátlan bicikli regisztráció',
            'Teljes szerviz történet',
            'Dokumentum tárolás',
            'Karbantartási emlékeztetők',
            'Keress megbízható szervizeket',
          ],
          cta: 'Regisztrálj ingyen',
          ctaLink: '/onboarding',
        },
        {
          name: 'Basic',
          price: '£6.99',
          period: '/hó',
          audience: 'Kisebb szervizeknek',
          highlighted: false,
          tier: 'basic',
          benefits: [
            'Manuális szerviz rögzítés',
            'Manuális bicikli felvétel',
            'Ügyfél kezelés',
            'Digitális Bicikli Útlevél',
            'Térképes megjelenés',
            'Ügyfélértékelések',
            'Alapszintű irányítópult',
          ],
          cta: 'Próba indítása',
          ctaLink: '/onboarding?plan=basic',
        },
        {
          name: 'Professional',
          price: '£20',
          period: '/hó',
          audience: 'AI számla feldolgozás',
          highlighted: true,
          badge: 'Legnépszerűbb',
          tier: 'professional',
          benefits: [
            'Minden Basic funkció',
            'AI elemek kinyerése fotó számlából',
            'Vázszám automatikus párosítás',
          ],
          cta: 'Próba indítása',
          ctaLink: '/onboarding?plan=professional',
        },
        {
          name: 'Enterprise',
          price: '£80',
          period: '/hó',
          audience: 'Teljes rendszer',
          highlighted: false,
          tier: 'enterprise',
          benefits: [
            'Minden Professional funkció',
            'Számlázás integráció',
            'Square, Stripe, Xero szinkron',
            'Online foglalasi rendszer',
            'Smart Technician Assignment',
            'Smart Work-Off Delegation',
            'E-mail & SMS marketing',
            'Több műhely & csapat kezelés',
            'Ajánlói program',
            'API hozzáférés & SLA',
          ],
          cta: 'Próba indítása',
          ctaLink: '/onboarding?plan=enterprise',
        },
        {
          name: 'Lifetime',
          price: '£10,000',
          period: 'egyszeri',
          audience: 'Örökre Enterprise',
          highlighted: false,
          tier: 'lifetime',
          badge: 'Legjobb érték',
          benefits: [
            'Minden Enterprise funkció',
            'Egyszeri fizetés, örökre',
            'Folyamatos frissítések',
            'Prioritásos támogatás',
            'Dedikált kapcsolattartó',
            'Soha többé havidíj',
          ],
          cta: 'Kapcsolatfelvétel',
          ctaLink: 'mailto:sales@frame-book.com?subject=Lifetime%20csomag%20érdeklődés',
        },
      ],
    },
    cta: {
      riders: {
        eyebrow: 'Kerékpárosoknak',
        title: 'Készítsd el biciklid útlevelét',
        body: 'Regisztrálj ingyen és tartsd karban biciklid történetét. Soha többé ne felejtsd el, mikor volt utoljára szervizelve.',
        primary: 'Ingyenes regisztráció',
        secondary: 'Tudj meg többet',
      },
      shops: {
        eyebrow: 'Szervizeknek',
        title: 'Digitalizáld műhelyed',
        body: 'Csatlakozz a Frame-Book hálózathoz és növeld ügyfélkörét modern, digitális szolgáltatásokkal.',
        primary: '14 napos próba',
        secondary: 'Árazás megtekintése',
      },
    },
    footer: {
      tagline: 'Bike Passport & Service History',
      contact: 'Kapcsolat',
      legal: 'Jogi',
      privacy: 'Adatvédelem',
      terms: 'Felhasználási feltételek',
    },
  },
  en: {
    nav: {
      features: 'Features',
      forRiders: 'For Riders',
      forShops: 'For Shops',
      pricing: 'Pricing',
      contact: 'Contact',
    },
    headerAuth: {
      login: 'Sign in',
      register: 'Register',
      dashboard: 'Back to App',
      logout: 'Logout',
    },
    hero: {
      badge: 'Bike Passport',
      title: 'The Global Standard',
      titleHighlight: 'in Bicycle Service',
      lead: 'Frame-Book is a digital ecosystem connecting bike owners and workshops. Get your bike\'s digital Bike Passport – completely free.',
      supporting: 'Clarity for riders. Credibility for workshops.',
      pills: [
        'Your Bike\'s Digital Service Passport',
        'Earn and display trust with every job',
        'Standardized service data worldwide',
      ],
      ctaPrimary: 'Register for free',
      ctaSecondary: 'Own a shop? Try it',
      ctaDemo: 'Try it now',
      ctaDemoSubtext: 'No registration needed',
    },
    stats: {
      bikes: 'Registered bikes',
      users: 'Happy cyclists',
      services: 'Services completed',
    },
    introVideo: {
      eyebrow: 'Introduction',
      title: 'Meet Frame-Book',
      subtitle: 'A quick intro by Denes',
    },
    audiences: {
      eyebrow: 'Who is it for?',
      title: 'One platform, two worlds',
      riders: {
        badge: 'Free',
        title: 'For Riders',
        subtitle: 'Your Bike\'s Digital Service Passport',
        description: 'Track your bike\'s complete service history – services, parts, documents. Get notified when it\'s time for maintenance.',
        benefits: [
          { icon: '📋', text: 'Complete bike passport' },
          { iconName: 'notifications', text: 'Maintenance reminders' },
          { icon: '📄', text: 'Digital receipts' },
          { icon: '🔒', text: 'Ownership verification' },
          { icon: '📸', text: 'Photos and documents' },
          { icon: '💬', text: 'Direct contact with shop' },
        ],
        cta: 'Register for free',
        price: 'Completely free, forever',
      },
      shops: {
        badge: 'Pro',
        title: 'For Workshops',
        subtitle: 'Earn and display trust with every job',
        description: 'Professional status and customer management system. Manage customers, bookings, marketing and billing from one interface. Increase loyalty and reduce administration.',
        benefits: [
          { iconName: 'calendar', text: 'Online booking system' },
          { iconName: 'users', text: 'Customer & bike database' },
          { iconName: 'email', text: 'Automatic reminders' },
          { iconName: 'analytics', text: 'Business analytics' },
          { icon: '💳', text: 'Billing and payments' },
          { icon: '📣', text: 'Marketing campaigns' },
        ],
        cta: 'Start with Basic for free',
        price: 'Basic is free to start',
      },
    },
    howItWorks: {
      eyebrow: 'How it works?',
      title: 'Simple as riding a bike',
      steps: [
        {
          number: '01',
          title: 'Register',
          description: 'Create your account in minutes, completely free.',
        },
        {
          number: '02',
          title: 'Add your bike',
          description: 'Record your bike\'s details, photos and documents.',
        },
        {
          number: '03',
          title: 'Choose a shop',
          description: 'Book an appointment with nearby partner shops.',
        },
        {
          number: '04',
          title: 'Track passport',
          description: 'Every service automatically appears in your bike passport.',
        },
      ],
    },
    features: {
      eyebrow: 'Features',
      title: 'Everything you need',
      groups: [
        {
          iconName: 'bike',
          title: 'Bike Profile',
          subtitle: 'All data in one place',
          bullets: [
            'Unique ID for every bike',
            'Photo & document uploads',
            'Ownership transfer tracking',
          ],
        },
        {
          icon: '🔧',
          title: 'Bike Passport',
          subtitle: 'Complete service history',
          bullets: [
            'Every repair detailed',
            'Installed parts list',
            'Receipts and invoices',
          ],
        },
        {
          iconName: 'calendar',
          title: 'Booking',
          subtitle: 'Online scheduling',
          bullets: [
            'Real-time availability',
            'Service selector',
            'Automatic confirmation',
          ],
        },
        {
          iconName: 'notifications',
          title: 'Reminders',
          subtitle: 'Never miss anything',
          bullets: [
            'Annual maintenance alerts',
            'Warranty expiration',
            'Seasonal tips',
          ],
        },
        {
          iconName: 'analytics',
          title: 'Analytics',
          subtitle: 'For shops',
          bullets: [
            'Customer statistics',
            'Revenue tracking',
            'Marketing effectiveness',
          ],
        },
        {
          icon: '🤝',
          title: 'Integration',
          subtitle: 'Connect everything',
          bullets: [
            'Google Calendar sync',
            'Email notifications',
          ],
        },
      ],
    },
    preview: {
      calendar: {
        title: 'Booking Calendar',
        description: 'View available slots and book online.',
      },
      passport: {
        title: 'Bike Passport',
        description: 'All services, parts and documents in one place.',
      },
      dashboard: {
        title: 'Dashboard',
        description: 'Overview of the most important information.',
      },
    },
    testimonials: {
      eyebrow: 'Testimonials',
      title: 'What people say?',
      items: [
        {
          quote: 'Finally I know when my bike was last serviced. Frame-Book completely changed how I maintain my bicycle.',
          author: 'James Wilson',
          role: 'Hobby cyclist',
        },
        {
          quote: 'My customers love that every service automatically shows up for them. And it saves me tons of time on administration.',
          author: 'Spoke & Pedal Cycles',
          role: 'Partner workshop',
        },
        {
          quote: 'I bought a used bike and could see its complete history. Worth every penny, I knew what I was investing in.',
          author: 'Anna Johnson',
          role: 'Urban cyclist',
        },
      ],
    },
    pricing: {
      eyebrow: 'Pricing',
      title: 'Simple and transparent',
      description: 'Free for cyclists. Affordable plans for workshops.',
      plans: [
        {
          name: 'Cyclist',
          price: 'Free',
          period: 'forever',
          audience: 'For bike owners',
          highlighted: false,
          tier: 'free',
          benefits: [
            'Unlimited bike registration',
            'Complete service history',
            'Document storage',
            'Maintenance reminders',
            'Find trusted workshops',
          ],
          cta: 'Register for free',
          ctaLink: '/onboarding',
        },
        {
          name: 'Basic',
          price: '£6.99',
          period: '/month',
          audience: 'For small workshops',
          highlighted: false,
          tier: 'basic',
          benefits: [
            'Manual service logging',
            'Manual bike registration',
            'Customer management',
            'Digital Bike Passport',
            'Public map listing',
            'Customer reviews',
            'Basic dashboard',
          ],
          cta: 'Start trial',
          ctaLink: '/onboarding?plan=basic',
        },
        {
          name: 'Professional',
          price: '£20',
          period: '/month',
          audience: 'AI receipt extraction',
          highlighted: true,
          badge: 'Most Popular',
          tier: 'professional',
          benefits: [
            'All Basic features',
            'AI item extraction from photo receipt',
            'Frame number auto-matching',
          ],
          cta: 'Start trial',
          ctaLink: '/onboarding?plan=professional',
        },
        {
          name: 'Enterprise',
          price: '£80',
          period: '/month',
          audience: 'Full business suite',
          highlighted: false,
          tier: 'enterprise',
          benefits: [
            'All Professional features',
            'Billing integration',
            'Square, Stripe, Xero sync',
            'Online booking system',
            'Smart Technician Assignment',
            'Smart Work-Off Delegation',
            'Email & SMS marketing',
            'Multi-location & team',
            'Referral program',
            'API access & SLA',
          ],
          cta: 'Start trial',
          ctaLink: '/onboarding?plan=enterprise',
        },
        {
          name: 'Lifetime',
          price: '£10,000',
          period: 'one-time',
          audience: 'Enterprise forever',
          highlighted: false,
          tier: 'lifetime',
          badge: 'Best Value',
          benefits: [
            'All Enterprise features',
            'One-time payment, forever',
            'Continuous updates',
            'Priority support',
            'Dedicated account manager',
            'Never pay monthly again',
          ],
          cta: 'Contact us',
          ctaLink: 'mailto:sales@frame-book.com?subject=Lifetime%20Plan%20Inquiry',
        },
      ],
    },
    cta: {
      riders: {
        eyebrow: 'For Cyclists',
        title: 'Create your bike passport',
        body: 'Register for free and keep track of your bike\'s history. Never forget when it was last serviced.',
        primary: 'Free registration',
        secondary: 'Learn more',
      },
      shops: {
        eyebrow: 'For Service Shops',
        title: 'Digitise your workshop',
        body: 'Join the Frame-Book network and grow your customer base with modern, digital services.',
        primary: 'Start with Basic for free',
        secondary: 'View pricing',
      },
    },
    footer: {
      tagline: 'Bike Passport & Service History',
      contact: 'Contact',
      legal: 'Legal',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
    },
  },
  de: {
    nav: {
      features: 'Funktionen',
      forRiders: 'Für Radfahrer',
      forShops: 'Für Werkstätten',
      pricing: 'Preise',
      contact: 'Kontakt',
    },
    headerAuth: {
      login: 'Anmelden',
      register: 'Registrieren',
      dashboard: 'Zurück zur App',
      logout: 'Abmelden',
    },
      hero: {
      badge: 'Fahrrad-Pass',
      title: 'Der Globale Standard',
      titleHighlight: 'im Fahrradservice',
      lead: 'Frame-Book ist ein digitales Ökosystem, das Fahrradbesitzer und Werkstätten verbindet. Erhalte den digitalen Bike Passport für dein Fahrrad – komplett kostenlos.',
      supporting: 'Klarheit für Radfahrer. Glaubwürdigkeit für Werkstätten.',
      pills: [
        'Dein Fahrrads Digitaler Service-Pass',
        'Verdiene und zeige Vertrauen mit jeder Arbeit',
        'Standardisierte Service-Daten weltweit',
      ],
      ctaPrimary: 'Kostenlos registrieren',
      ctaSecondary: 'Werkstatt? Jetzt testen',
      ctaDemo: 'Jetzt ausprobieren',
      ctaDemoSubtext: 'Ohne Registrierung',
    },
    stats: {
      bikes: 'Registrierte Fahrräder',
      users: 'Zufriedene Radfahrer',
      services: 'Durchgeführte Services',
    },
    introVideo: {
      eyebrow: 'Einführung',
      title: 'Frame-Book kennenlernen',
      subtitle: 'Kurze Vorstellung von Denes',
    },
    audiences: {
      eyebrow: 'Für wen ist es?',
      title: 'Eine Plattform, zwei Welten',
      riders: {
        badge: 'Kostenlos',
        title: 'Für Radfahrer',
        subtitle: 'Dein Fahrrads Digitaler Service-Pass',
        description: 'Verfolge die komplette Service-Historie deines Fahrrads – Services, Teile, Dokumente. Werde benachrichtigt, wenn Wartung fällig ist.',
        benefits: [
          { icon: '📋', text: 'Kompletter Fahrrad-Pass' },
          { iconName: 'notifications', text: 'Wartungserinnerungen' },
          { icon: '📄', text: 'Digitale Belege' },
          { icon: '🔒', text: 'Eigentumsnachweis' },
          { icon: '📸', text: 'Fotos und Dokumente' },
          { icon: '💬', text: 'Direkter Kontakt zur Werkstatt' },
        ],
        cta: 'Kostenlos registrieren',
        price: 'Komplett kostenlos, für immer',
      },
      shops: {
        badge: 'Pro',
        title: 'Für Werkstätten',
        subtitle: 'Verdiene und zeige Vertrauen mit jeder Arbeit',
        description: 'Professioneller Status und Kundenverwaltungssystem. Verwalte Kunden, Buchungen, Marketing und Abrechnung von einer Oberfläche. Steigere Loyalität und reduziere Verwaltung.',
        benefits: [
          { iconName: 'calendar', text: 'Online-Buchungssystem' },
          { iconName: 'users', text: 'Kunden- & Fahrraddatenbank' },
          { iconName: 'email', text: 'Automatische Erinnerungen' },
          { iconName: 'analytics', text: 'Geschäftsanalysen' },
          { icon: '💳', text: 'Abrechnung und Zahlungen' },
          { icon: '📣', text: 'Marketing-Kampagnen' },
        ],
        cta: '14-tägige Testphase starten',
        price: 'Ab £6.99/Monat',
      },
    },
    howItWorks: {
      eyebrow: 'Wie funktioniert es?',
      title: 'Einfach wie Radfahren',
      steps: [
        {
          number: '01',
          title: 'Registrieren',
          description: 'Erstelle dein Konto in Minuten, komplett kostenlos.',
        },
        {
          number: '02',
          title: 'Fahrrad hinzufügen',
          description: 'Erfasse die Daten, Fotos und Dokumente deines Fahrrads.',
        },
        {
          number: '03',
          title: 'Werkstatt wählen',
          description: 'Buche einen Termin bei nahen Partnerwerkstätten.',
        },
        {
          number: '04',
          title: 'Historie verfolgen',
          description: 'Jeder Service erscheint automatisch in deinem Pass.',
        },
      ],
    },
    features: {
      eyebrow: 'Funktionen',
      title: 'Alles was du brauchst',
      groups: [
        {
          icon: '🚲',
          title: 'Fahrrad-Profil',
          subtitle: 'Alle Daten an einem Ort',
          bullets: [
            'Eindeutige ID für jedes Fahrrad',
            'Foto- & Dokumenten-Upload',
            'Eigentümerwechsel-Tracking',
          ],
        },
        {
          icon: '🔧',
          title: 'Service-Protokoll',
          subtitle: 'Komplette Historie',
          bullets: [
            'Jede Reparatur detailliert',
            'Liste verbauter Teile',
            'Belege und Rechnungen',
          ],
        },
        {
          iconName: 'calendar',
          title: 'Buchung',
          subtitle: 'Online-Terminplanung',
          bullets: [
            'Echtzeit-Verfügbarkeit',
            'Service-Auswahl',
            'Automatische Bestätigung',
          ],
        },
        {
          iconName: 'notifications',
          title: 'Erinnerungen',
          subtitle: 'Nichts verpassen',
          bullets: [
            'Jährliche Wartungsalarme',
            'Garantieablauf',
            'Saisonale Tipps',
          ],
        },
        {
          icon: '📊',
          title: 'Analytik',
          subtitle: 'Für Werkstätten',
          bullets: [
            'Kundenstatistiken',
            'Umsatzverfolgung',
            'Marketing-Effektivität',
          ],
        },
        {
          icon: '🤝',
          title: 'Integration',
          subtitle: 'Alles verbinden',
          bullets: [
            'Google Calendar Sync',
            'E-Mail-Benachrichtigungen',
          ],
        },
      ],
    },
    preview: {
      calendar: {
        title: 'Buchungskalender',
        description: 'Verfügbare Termine ansehen und online buchen.',
      },
      passport: {
        title: 'Fahrrad-Pass',
        description: 'Alle Services, Teile und Dokumente an einem Ort.',
      },
      dashboard: {
        title: 'Dashboard',
        description: 'Übersicht der wichtigsten Informationen.',
      },
    },
    testimonials: {
      eyebrow: 'Bewertungen',
      title: 'Was sagen die Nutzer?',
      items: [
        {
          quote: 'Endlich weiß ich, wann mein Fahrrad zuletzt gewartet wurde. Frame-Book hat komplett verändert, wie ich mein Fahrrad pflege.',
          author: 'Peter Müller',
          role: 'Hobby-Radfahrer',
        },
        {
          quote: 'Meine Kunden lieben es, dass jeder Service automatisch bei ihnen angezeigt wird. Und es spart mir viel Zeit bei der Verwaltung.',
          author: 'Radwerk Berlin GmbH',
          role: 'Partnerwerkstatt',
        },
        {
          quote: 'Ich habe ein gebrauchtes Fahrrad gekauft und konnte die komplette Historie sehen. Jeden Cent wert, ich wusste, worin ich investiere.',
          author: 'Anna Weber',
          role: 'Stadt-Radfahrerin',
        },
      ],
    },
    pricing: {
      eyebrow: 'Preise',
      title: 'Einfach und transparent',
      description: 'Kostenlos für Radfahrer. Günstige Tarife für Werkstätten.',
      plans: [
        {
          name: 'Radfahrer',
          price: 'Kostenlos',
          period: 'für immer',
          audience: 'Für Fahrradbesitzer',
          highlighted: false,
          tier: 'free',
          benefits: [
            'Unbegrenzte Fahrradregistrierung',
            'Komplette Service-Historie',
            'Dokumentenspeicher',
            'Wartungserinnerungen',
            'Vertrauenswürdige Werkstätten finden',
          ],
          cta: 'Kostenlos registrieren',
          ctaLink: '/onboarding',
        },
        {
          name: 'Basic',
          price: '£6.99',
          period: '/Monat',
          audience: 'Für kleine Werkstätten',
          highlighted: false,
          tier: 'basic',
          benefits: [
            'Manuelle Service-Erfassung',
            'Manuelle Fahrrad-Registrierung',
            'Kundenverwaltung',
            'Digitaler Fahrrad-Pass',
            'Kartenlistung',
            'Kundenbewertungen',
            'Basis-Dashboard',
          ],
          cta: 'Testphase starten',
          ctaLink: '/onboarding?plan=basic',
        },
        {
          name: 'Professional',
          price: '£20',
          period: '/Monat',
          audience: 'KI Rechnungs-Extraktion',
          highlighted: true,
          badge: 'Am beliebtesten',
          tier: 'professional',
          benefits: [
            'Alle Basic-Funktionen',
            'KI Artikel-Extraktion aus Foto-Rechnung',
            'Rahmennummer-Auto-Matching',
          ],
          cta: 'Testphase starten',
          ctaLink: '/onboarding?plan=professional',
        },
        {
          name: 'Enterprise',
          price: '£80',
          period: '/Monat',
          audience: 'Komplettes Business-Paket',
          highlighted: false,
          tier: 'enterprise',
          benefits: [
            'Alle Professional-Funktionen',
            'Abrechnungs-Integration',
            'Square, Stripe, Xero Sync',
            'Online-Buchungssystem',
            'Smart Technician Assignment',
            'Smart Work-Off Delegation',
            'E-Mail & SMS Marketing',
            'Multi-Standort & Team',
            'Empfehlungsprogramm',
            'API-Zugang & SLA',
          ],
          cta: 'Testphase starten',
          ctaLink: '/onboarding?plan=enterprise',
        },
        {
          name: 'Lifetime',
          price: '£10.000',
          period: 'einmalig',
          audience: 'Enterprise für immer',
          highlighted: false,
          tier: 'lifetime',
          badge: 'Bester Wert',
          benefits: [
            'Alle Enterprise-Funktionen',
            'Einmalzahlung, für immer',
            'Kontinuierliche Updates',
            'Prioritäts-Support',
            'Dedizierter Ansprechpartner',
            'Nie wieder Monatsgebühr',
          ],
          cta: 'Kontaktieren',
          ctaLink: 'mailto:sales@frame-book.com?subject=Lifetime%20Paket%20Anfrage',
        },
      ],
    },
    cta: {
      riders: {
        eyebrow: 'Für Radfahrer',
        title: 'Erstelle deinen Fahrrad-Pass',
        body: 'Registriere dich kostenlos und verfolge die Geschichte deines Fahrrads. Vergiss nie wieder, wann es zuletzt gewartet wurde.',
        primary: 'Kostenlose Registrierung',
        secondary: 'Mehr erfahren',
      },
      shops: {
        eyebrow: 'Für Werkstätten',
        title: 'Digitalisiere deine Werkstatt',
        body: 'Tritt dem Frame-Book-Netzwerk bei und vergrößere deinen Kundenstamm mit modernen, digitalen Services.',
        primary: '14-tägige Testphase',
        secondary: 'Preise ansehen',
      },
    },
    footer: {
      tagline: 'Bike Passport & Service History',
      contact: 'Kontakt',
      legal: 'Rechtliches',
      privacy: 'Datenschutz',
      terms: 'Nutzungsbedingungen',
    },
  },
};

const themeLabels = {
  day: { label: { hu: 'Nappali', en: 'Day', de: 'Tag' } },
  night: { label: { hu: 'Éjszakai', en: 'Night', de: 'Nacht' } },
};

const LANDING_THEME_STORAGE_KEY = 'landingPageTheme';

const getStoredLandingPageTheme = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }
  const saved = window.localStorage.getItem(LANDING_THEME_STORAGE_KEY);
  return saved === 'day' || saved === 'night' ? saved : null;
};

const setStoredLandingPageTheme = (value) => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  window.localStorage.setItem(LANDING_THEME_STORAGE_KEY, value);
};

const getSystemLandingTheme = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'day';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'night' : 'day';
};

const getInitialLandingRoleFromQuery = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  const role = new URLSearchParams(window.location.search).get('role');
  if (role === 'cyclist' || role === 'shop') {
    return role;
  }
  return null;
};

// Helper function to convert emoji to icon name
const getIconNameFromEmoji = (emoji) => {
  const emojiMap = {
    '📅': 'calendar',
    '👥': 'users',
    '📧': 'email',
    '📊': 'analytics',
    '🚲': 'bike',
    '🔧': 'service',
    '🔔': 'notifications',
    '🤝': 'integration',
    '📋': 'details',
    '📄': 'documents',
    '🔒': 'verified',
    '📸': 'photos',
    '💬': 'chat',
    '💳': 'billing',
    '📣': 'campaign',
  };
  return emojiMap[emoji] || 'help';
};

function LandingPage({ user, onLogout, onLogin }) {
  const initialRole = getInitialLandingRoleFromQuery();
  const [theme, setTheme] = useState(() => {
    const storedTheme = getStoredLandingPageTheme();
    return storedTheme || getSystemLandingTheme();
  });
  const [hasManualThemePreference, setHasManualThemePreference] = useState(() => getStoredLandingPageTheme() !== null);
  const language = 'en'; // English only
  const [menuOpen, setMenuOpen] = useState(false);
  const [activePreview, setActivePreview] = useState(initialRole === 'shop' ? 'dashboard' : 'passport');
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [demoLoading, setDemoLoading] = useState(false);
  const [detailsModal, setDetailsModal] = useState({ open: false, tier: null });
  const [selectedRole, setSelectedRole] = useState(initialRole || (user ? 'cyclist' : null));
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(!initialRole && !user);
  const [expandedShopFeatureId, setExpandedShopFeatureId] = useState('booking_calendar');
  const shopFeatureBodyRefs = useRef({});
  const { logoUrl: appLogoUrl } = useAppLogo(theme);
  const logoUrl = `${process.env.PUBLIC_URL || ''}/logo.png` || appLogoUrl;
  const themePreferenceOverrideRef = useRef(hasManualThemePreference);
  const pricingRef = useRef(null);
  const featuresRef = useRef(null);
  const navigate = useNavigate();

  // Same demo credentials as on the login page: one username + one password,
  // optionally overridable from env, de alapértelmezésben fix párt használunk.
  const DEMO_USERNAME = process.env.REACT_APP_DEMO_USERNAME || 'demo';
  const DEMO_PASSWORD = process.env.REACT_APP_DEMO_PASSWORD || 'demo123';

  const isSmartEnterprisePoint = (value = '') => {
    const normalized = String(value).toLowerCase();
    return (
      normalized.includes('smart technician assignment') ||
      normalized.includes('smart technical assignment') ||
      normalized.includes('smart work-off delegation') ||
      normalized.includes('smart work off delegation') ||
      normalized.includes('ai item extraction from photo receipt') ||
      normalized.includes('ai elemek kinyerése fotó számlából') ||
      normalized.includes('ki artikel-extraktion aus foto-rechnung')
    );
  };

  // Handle "Try Demo" button click - auto-login with demo account
  const handleTryDemo = async () => {
    setDemoLoading(true);
    try {
      // Demo account credentials - keep in sync with LoginPage demo defaults
      const response = await authAPI.login(DEMO_USERNAME, DEMO_PASSWORD, { demo: true });
      if (onLogin) {
        onLogin(response.data.user, response.data.requires_setup);
      }
      // Enable demo mode in window for banner display
      if (typeof window !== 'undefined') {
        window.__DEMO_MODE__ = true;
        window.__DEMO_USERNAME__ = DEMO_USERNAME;
        window.__DEMO_PASSWORD__ = DEMO_PASSWORD;
        window.__DEMO_TOOLTIP__ = 'This is a live demo. Changes will not be saved.';
      }
      navigate('/dashboard');
    } catch (err) {
      console.error('Demo login failed:', err);
      // Fallback: navigate to login page with demo hint
      navigate('/login?demo=true');
    } finally {
      setDemoLoading(false);
    }
  };
  
  const copy = translations[language] || translations.hu;
  const selectorCopy = {
    eyebrow: language === 'hu' ? 'Válassz nézetet' : language === 'de' ? 'Wähle deinen Weg' : 'Choose your path',
    title: language === 'hu' ? 'Mi érdekel most?' : language === 'de' ? 'Was interessiert dich zuerst?' : 'What are you interested in first?',
    subtitle: language === 'hu'
      ? 'Válaszd ki, hogy kerékpárosként vagy szervizként néznéd meg a Frame-Book funkciókat.'
      : language === 'de'
        ? 'Wähle aus, ob du die Funktionen für Radfahrer oder Werkstätten sehen möchtest.'
        : 'Pick whether you want to see features for cyclists or for bike shops.',
    cyclistTitle: language === 'hu' ? "I'm a cyclist" : language === 'de' ? 'I\'m a cyclist' : "I'm a cyclist",
    cyclistDescription: language === 'hu'
      ? 'Bicikli útlevél, szerviztörténet, emlékeztetők és megbízható műhelyek érdekelnek.'
      : language === 'de'
        ? 'Ich möchte Bike Passport, Servicehistorie, Erinnerungen und Werkstätten sehen.'
        : 'I want bike passport, service history, reminders and trusted workshops.',
    cyclistCta: language === 'hu' ? 'Mutasd a kerékpáros funkciókat' : language === 'de' ? 'Zeige Radfahrer-Funktionen' : 'Show cyclist features',
    shopTitle: language === 'hu' ? "I'm a bike shop / bike service" : language === 'de' ? 'I\'m a bike shop / bike service' : "I'm a bike shop / bike service",
    shopDescription: language === 'hu'
      ? 'Ügyfélkezelés, online foglalás, AI számla-feldolgozás és üzleti funkciók érdekelnek.'
      : language === 'de'
        ? 'Ich möchte Kundenverwaltung, Buchungen, KI-Beleg-Erfassung und Business-Tools.'
        : 'I want customer management, bookings, AI receipt extraction and business tools.',
    shopCta: language === 'hu' ? 'Mutasd a szerviz funkciókat' : language === 'de' ? 'Zeige Werkstatt-Funktionen' : 'Show shop features',
  };
  const isCyclistView = selectedRole === 'cyclist';
  const isShopView = selectedRole === 'shop';
  const activeHero = isShopView ? copy.audiences.shops : copy.audiences.riders;
  const roleHeroBadge = isShopView
    ? (language === 'hu' ? 'Bike Shop Mode' : language === 'de' ? 'Werkstatt-Modus' : 'Bike Shop Mode')
    : (language === 'hu' ? 'Cyclist Mode' : language === 'de' ? 'Radfahrer-Modus' : 'Cyclist Mode');
  const roleHeroPrimaryLink = isShopView ? '/onboarding?plan=professional' : '/onboarding';
  const roleHeroPrimaryLabel = activeHero.cta;
  const roleHeroSecondaryLabel = isShopView
    ? (language === 'hu' ? 'View pricing' : language === 'de' ? 'Preise ansehen' : 'View pricing')
    : (language === 'hu' ? 'Features' : language === 'de' ? 'Features' : 'Features');
  const roleHeroPills = activeHero.benefits.slice(0, 3).map((benefit) => benefit.text);
  const visiblePreviewTabs = isShopView ? ['dashboard', 'calendar'] : ['passport', 'calendar'];
  const bikePassportImage = `${process.env.PUBLIC_URL || ''}/images/bike-passport-real.png`;
  const cyclistPassportHighlights = [
    'Full bike identity with frame number and model details',
    'Chronological service history with parts and costs',
    'Workshop-linked entries to increase trust when selling',
    'Documents and proof of maintenance in one place',
  ];
  const roleStats = isShopView
    ? [
      { value: '312', label: language === 'hu' ? 'Aktív szerviz' : language === 'de' ? 'Aktive Werkstätten' : 'Active workshops' },
      { value: '12,904', label: language === 'hu' ? 'Lezárt munkalap' : language === 'de' ? 'Abgeschlossene Jobs' : 'Completed jobs' },
      { value: '4.9/5', label: language === 'hu' ? 'Ügyfél elégedettség' : language === 'de' ? 'Kundenzufriedenheit' : 'Customer satisfaction' },
    ]
    : [
      { value: '2,847', label: copy.stats.bikes },
      { value: '1,523', label: copy.stats.users },
      { value: '8,421', label: copy.stats.services },
    ];
  const shopHowItWorksSteps = [
    {
      number: '01',
      title: language === 'hu' ? 'Regisztráld a műhelyed' : language === 'de' ? 'Werkstatt registrieren' : 'Register your workshop',
      description: language === 'hu' ? 'Állítsd be a szolgáltatásokat, nyitvatartást és csapatot.' : language === 'de' ? 'Leistungen, Öffnungszeiten und Team einrichten.' : 'Set up services, opening hours, and your team.',
    },
    {
      number: '02',
      title: language === 'hu' ? 'Fogadjatok foglalásokat' : language === 'de' ? 'Buchungen annehmen' : 'Accept bookings',
      description: language === 'hu' ? 'Online időpontok, munkalapok és ügyféladatok egy helyen.' : language === 'de' ? 'Online-Termine, Jobs und Kundendaten an einem Ort.' : 'Online appointments, jobs, and customer records in one place.',
    },
    {
      number: '03',
      title: language === 'hu' ? 'Rögzítsd a munkát gyorsan' : language === 'de' ? 'Arbeiten schnell erfassen' : 'Log work fast',
      description: language === 'hu' ? 'Szervizek, alkatrészek, számlák és státuszok valós időben.' : language === 'de' ? 'Services, Teile, Rechnungen und Status in Echtzeit.' : 'Services, parts, invoices, and statuses in real time.',
    },
    {
      number: '04',
      title: language === 'hu' ? 'Növeld a bevételt' : language === 'de' ? 'Umsatz steigern' : 'Grow revenue',
      description: language === 'hu' ? 'Analitika, marketing és csapat teljesítmény egy dashboardon.' : language === 'de' ? 'Analytics, Marketing und Team-Performance in einem Dashboard.' : 'Analytics, marketing, and team performance in one dashboard.',
    },
  ];
  const activeHowItWorks = isShopView ? shopHowItWorksSteps : copy.howItWorks.steps;
  const activeHowItWorksTitle = copy.howItWorks.title;
  const shopFeatureCatalog = [
    { id: 'booking_calendar', name: 'Online Booking Calendar', description: 'Give customers a real booking flow with live slot visibility, service-type selection, confirmation handling, and conflict-safe scheduling. The calendar view helps your team balance workload across the day and avoid overbooking.' },
    { id: 'service_tickets', name: 'Service Ticket Workflow', description: 'Track every job from intake to pickup with clear statuses, timestamps, notes, and assignment history. Tickets centralize communication and reduce missed steps during busy workshop days.' },
    { id: 'customer_database', name: 'Customer & Bike Database', description: 'Maintain full customer profiles and linked bikes with frame numbers, specs, and historical records. This enables fast lookup at check-in and better repeat-service experience.' },
    { id: 'technician_management', name: 'Technician Management', description: 'Configure workshop users, roles, permissions, and job ownership. Assign work by availability and responsibility so each ticket has accountable execution.' },
    { id: 'service_templates', name: 'Service Templates', description: 'Create reusable service packages with predefined tasks, durations, and pricing logic. Templates speed up quoting and keep your service quality consistent between technicians.' },
    { id: 'service_rooms', name: 'Service Rooms / Bays', description: 'Model physical service capacity using work bays/rooms and schedule against actual resources. This prevents booking collisions and keeps operational flow realistic.' },
    { id: 'receipt_ai', name: 'AI Receipt Extraction', description: 'Upload receipt/invoice photos and extract service lines, parts, and amounts automatically. This reduces manual data entry and helps keep records complete even under time pressure.' },
    { id: 'email_automation', name: 'Email Notifications & Reminders', description: 'Automate customer communication for booking confirmations, service status changes, and pickup readiness. Consistent outbound messages reduce no-shows and support calls.' },
    { id: 'billing_integrations', name: 'Billing Integrations', description: 'Connect billing/payment systems and align service completion with invoicing workflows. Integration minimizes duplicate admin work and improves revenue traceability.' },
    { id: 'analytics_dashboard', name: 'Analytics Dashboard', description: 'Monitor workshop KPIs like booking volume, completion throughput, technician load, and revenue trend. Use these metrics to tune staffing and increase profitability.' },
    { id: 'reviews_management', name: 'Review Management', description: 'Collect customer feedback tied to real service experiences and respond from one place. Strong review management improves public trust and conversion for new customers.' },
    { id: 'marketing_campaigns', name: 'Marketing Campaigns', description: 'Run targeted campaigns to reactivate inactive customers, promote seasonal services, and retain your base. Campaign tools help move beyond one-off jobs to recurring business.' },
    { id: 'team_time_off', name: 'Time-Off & Delegation', description: 'Handle leave requests and automatically rebalance pending workload where possible. Delegation support keeps customer commitments reliable even when staff availability changes.' },
  ];
  const shopFeatureColumns = shopFeatureCatalog.reduce((columns, feature, index) => {
    columns[index % 2].push(feature);
    return columns;
  }, [[], []]);
  const visibleFeatureGroups = copy.features.groups.filter((feature) => {
    if (!selectedRole) {
      return true;
    }
    const featureType = feature.iconName || getIconNameFromEmoji(feature.icon || '');
    const shopFeatureTypes = ['analytics', 'integration', 'calendar', 'service'];
    const cyclistFeatureTypes = ['calendar', 'notifications', 'integration'];

    if (selectedRole === 'shop') {
      return shopFeatureTypes.includes(featureType);
    }

    return cyclistFeatureTypes.includes(featureType);
  });
  const isBasicCampaignActive = isCompanyBasicFreeCampaignActive();
  const pricingPlans = copy.pricing.plans.map((plan) => {
    if (!isBasicCampaignActive || plan.tier !== 'basic') {
      return plan;
    }

    const localizedBadge = language === 'hu'
      ? `Ingyenes ${COMPANY_BASIC_FREE_CAMPAIGN_END_LABEL}-ig`
      : language === 'de'
        ? 'Kostenlos bis 31.12.2026'
        : 'Free Until Dec 31, 2026';

    const localizedCta = language === 'hu'
      ? 'Ingyenes indulás'
      : language === 'de'
        ? 'Kostenlos starten'
        : 'Start for free';

    const localizedCampaignText = language === 'hu'
      ? `Céges Basic regisztráció ingyenes ${COMPANY_BASIC_FREE_CAMPAIGN_END_LABEL}-ig.`
      : language === 'de'
        ? 'Basic-Registrierung für Werkstätten ist bis 31.12.2026 kostenlos.'
        : `Company Basic registration is free until ${COMPANY_BASIC_FREE_CAMPAIGN_END_LABEL}.`;

    return {
      ...plan,
      price: '£0',
      originalPrice: plan.price,
      badge: localizedBadge,
      cta: localizedCta,
      campaignText: localizedCampaignText,
    };
  });

  useEffect(() => {
    themePreferenceOverrideRef.current = hasManualThemePreference;
  }, [hasManualThemePreference]);

  // If user is logged in, skip role selector and default to cyclist
  useEffect(() => {
    if (user) {
      setIsRoleModalOpen(false);
      setSelectedRole((prev) => prev || 'cyclist');
    }
  }, [user]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = isRoleModalOpen ? 'hidden' : previousOverflow;
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isRoleModalOpen]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = (event) => {
      if (themePreferenceOverrideRef.current) {
        return;
      }
      setTheme(event.matches ? 'night' : 'day');
    };

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleSystemThemeChange);
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(handleSystemThemeChange);
    }

    return () => {
      if (typeof mediaQuery.removeEventListener === 'function') {
        mediaQuery.removeEventListener('change', handleSystemThemeChange);
      } else if (typeof mediaQuery.removeListener === 'function') {
        mediaQuery.removeListener(handleSystemThemeChange);
      }
    };
  }, []);

  const handleThemeSelection = (mode) => {
    setTheme(mode);
    setHasManualThemePreference(true);
    setStoredLandingPageTheme(mode);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      { threshold: 0.15 }
    );

    const elements = document.querySelectorAll('.reveal-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [selectedRole]);

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % copy.testimonials.items.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [copy.testimonials.items.length]);

  const closeMenu = () => setMenuOpen(false);

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: 'smooth' });
    closeMenu();
  };

  const handleNavClick = (section) => {
    switch (section) {
      case 'features':
        scrollToSection(featuresRef);
        break;
      case 'riders':
        setSelectedRole('cyclist');
        setIsRoleModalOpen(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        break;
      case 'shops':
        setSelectedRole('shop');
        setIsRoleModalOpen(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        break;
      case 'pricing':
        scrollToSection(pricingRef);
        break;
      default:
        break;
    }
  };

  const handleRoleSelection = (role) => {
    setSelectedRole(role);
    setIsRoleModalOpen(false);
    if (role === 'cyclist') {
      setActivePreview('passport');
      return;
    }

    setActivePreview('dashboard');
  };

  return (
    <div className={`landing-page ${theme === 'night' ? 'theme-night' : 'theme-day'}`}>
      {/* Header */}
      <header className="landing-header">
        <div className="logo-cluster">
          {logoUrl && (
            <img src={logoUrl} alt="Frame-Book Logo" className="main-logo" />
          )}
        </div>

        <nav className="nav-links">
          <button type="button" onClick={() => handleNavClick('features')}>
            {copy.nav.features}
          </button>
          <button type="button" onClick={() => handleNavClick('riders')}>
            {copy.nav.forRiders}
          </button>
          <button type="button" onClick={() => handleNavClick('shops')}>
            {copy.nav.forShops}
          </button>
          <button type="button" onClick={() => handleNavClick('pricing')}>
            {copy.nav.pricing}
          </button>
        </nav>

        <div className="header-meta">
          <div className="contact-stack">
            <a href="tel:+441392343922">44 1392 343922</a>
            <span style={{ fontSize: '11px', opacity: 0.7 }}>Call anytime between 7am and 9pm</span>
            <a href="mailto:support@frame-book.com">support@frame-book.com</a>
          </div>
          <div className="language-auth">
            {user ? (
              <>
                <Link to="/dashboard" className="btn-primary small">
                  {copy.headerAuth.dashboard}
                </Link>
              </>
            ) : (
              <>
                <Link to="/login" className="auth-link">
                  {copy.headerAuth.login}
                </Link>
                <Link to="/onboarding" className="btn-primary small">
                  {copy.headerAuth.register}
                </Link>
              </>
            )}
          </div>
          <button
            type="button"
            className="mobile-menu-toggle"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(true)}
          >
            <span />
            <span />
            <span />
            <span className="sr-only">Open menu</span>
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${menuOpen ? 'open' : ''}`}>
        <button type="button" className="mobile-menu-backdrop" onClick={closeMenu} aria-label="Close menu" />
        <div className="mobile-menu-panel">
          <div className="mobile-menu-head">
            <strong>Frame-Book</strong>
            <button type="button" onClick={closeMenu} aria-label="Close menu">
              ×
            </button>
          </div>
          <nav className="mobile-nav-links">
            <button type="button" onClick={() => handleNavClick('features')}>
              {copy.nav.features}
            </button>
            <button type="button" onClick={() => handleNavClick('riders')}>
              {copy.nav.forRiders}
            </button>
            <button type="button" onClick={() => handleNavClick('shops')}>
              {copy.nav.forShops}
            </button>
            <button type="button" onClick={() => handleNavClick('pricing')}>
              {copy.nav.pricing}
            </button>
          </nav>
          <div className="mobile-auth-buttons">
            {user ? (
              <Link to="/dashboard" className="btn-primary" onClick={closeMenu}>
                {copy.headerAuth.dashboard}
              </Link>
            ) : (
              <>
                <Link to="/login" className="btn-ghost" onClick={closeMenu}>
                  {copy.headerAuth.login}
                </Link>
                <Link to="/onboarding" className="btn-primary" onClick={closeMenu}>
                  {copy.headerAuth.register}
                </Link>
              </>
            )}
          </div>
          <div className="mobile-contact">
            <a href="tel:+441392343922">44 1392 343922</a>
            <span style={{ fontSize: '11px', opacity: 0.7 }}>7am – 9pm</span>
            <a href="mailto:support@frame-book.com">support@frame-book.com</a>
          </div>
        </div>
      </div>

      {isRoleModalOpen && (
        <div className="role-gate-overlay">
          <div className="role-gate-modal">
            <div className="role-gate-header">
              <span className="eyebrow">{selectorCopy.eyebrow}</span>
              <h2>{selectorCopy.title}</h2>
              <p>{selectorCopy.subtitle}</p>
            </div>
            <div className="role-gate-grid">
              <button type="button" className="role-gate-card cyclist" onClick={() => handleRoleSelection('cyclist')}>
                <span className="role-gate-icon">
                  <Icon name="bike" color="brand" darkMode={theme === 'night'} size={34} />
                </span>
                <h3>{selectorCopy.cyclistTitle}</h3>
                <p>{selectorCopy.cyclistDescription}</p>
                <span className="role-gate-cta">{selectorCopy.cyclistCta}</span>
              </button>
              <button type="button" className="role-gate-card shop" onClick={() => handleRoleSelection('shop')}>
                <span className="role-gate-icon">
                  <Icon name="shops" color="brand" darkMode={theme === 'night'} size={34} />
                </span>
                <h3>{selectorCopy.shopTitle}</h3>
                <p>{selectorCopy.shopDescription}</p>
                <span className="role-gate-cta">{selectorCopy.shopCta}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Theme Toggle - Removed by request */}

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content reveal-on-scroll">
          <span className="hero-badge">
            <Icon name="bikePassports" color="brand" darkMode={theme === 'night'} size={18} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
            {roleHeroBadge}
          </span>
          <h1>
            {activeHero.title}
            {(isCyclistView || isShopView) ? (
              <>
                <br />
                <span className="highlight">{activeHero.subtitle}</span>
              </>
            ) : (
              <> <span className="highlight">{activeHero.subtitle}</span></>
            )}
          </h1>
          <p className="hero-lead">{activeHero.description}</p>
          
          <ul className="hero-pills">
            {roleHeroPills.map((pill) => (
              <li key={pill}>
                <span className="pill-check">
                  <Icon name="success" color="brand" darkMode={theme === 'night'} size={16} />
                </span>
                {pill}
              </li>
            ))}
          </ul>

          <div className="hero-cta">
            {user ? (
              <Link to="/dashboard" className="btn-primary btn-large">
                {copy.headerAuth.dashboard}
              </Link>
            ) : (
              <>
                <Link to={roleHeroPrimaryLink} className="btn-primary btn-large">
                  {roleHeroPrimaryLabel}
                </Link>
                {isShopView ? (
                  <button type="button" className="btn-ghost btn-large" onClick={() => handleNavClick('pricing')}>
                    {roleHeroSecondaryLabel}
                  </button>
                ) : (
                  <button type="button" className="btn-ghost btn-large" onClick={() => handleNavClick('features')}>
                    {roleHeroSecondaryLabel}
                  </button>
                )}
              </>
            )}
          </div>

          <p className="hero-supporting">{activeHero.price}</p>
        </div>

        <div className="hero-visual reveal-on-scroll">
          <div className="preview-tabs">
            {visiblePreviewTabs.includes('passport') && (
              <button
                className={activePreview === 'passport' ? 'active' : ''}
                onClick={() => setActivePreview('passport')}
                type="button"
              >
                {copy.preview.passport.title}
              </button>
            )}
            {visiblePreviewTabs.includes('calendar') && (
              <button
                className={activePreview === 'calendar' ? 'active' : ''}
                onClick={() => setActivePreview('calendar')}
                type="button"
              >
                {copy.preview.calendar.title}
              </button>
            )}
            {visiblePreviewTabs.includes('dashboard') && (
              <button
                className={activePreview === 'dashboard' ? 'active' : ''}
                onClick={() => setActivePreview('dashboard')}
                type="button"
              >
                {copy.preview.dashboard.title}
              </button>
            )}
          </div>

          <div className="preview-container">
            {/* Bike Passport - Based on BikeDetailPage */}
            {activePreview === 'passport' && (
              <div className="preview-passport">
                <div className="passport-header">
                  <div className="passport-bike-avatar">
                    <Icon name="bike" customColor="#f5ffe8" size={32} />
                  </div>
                  <div className="passport-bike-info">
                    <h3>Canyon Endurace CF SL</h3>
                    <span className="passport-id">Frame: FRB2024X8K7J</span>
                  </div>
                  <span className="passport-year">2024</span>
                </div>
                
                {/* Stats like real app */}
                <div className="passport-stats">
                  <div className="stat-card-mini">
                    <span className="stat-icon">
                      <Icon name="bike" color="brand" darkMode={theme === 'night'} size={20} />
                    </span>
                    <div><span className="stat-value">3</span><span className="stat-label">{language === 'hu' ? 'Bicikli' : language === 'de' ? 'Räder' : 'Bikes'}</span></div>
                  </div>
                  <div className="stat-card-mini">
                    <span className="stat-icon">
                      <Icon name="service" color="brand" darkMode={theme === 'night'} size={20} />
                    </span>
                    <div><span className="stat-value">12</span><span className="stat-label">{language === 'hu' ? 'Szerviz' : 'Services'}</span></div>
                  </div>
                  <div className="stat-card-mini">
                    <span className="stat-icon">
                      <Icon name="shops" color="brand" darkMode={theme === 'night'} size={20} />
                    </span>
                    <div><span className="stat-value">2</span><span className="stat-label">{language === 'hu' ? 'Műhely' : language === 'de' ? 'Werkstätten' : 'Shops'}</span></div>
                  </div>
                </div>

                {/* Service History like real app */}
                <div className="passport-services">
                  <h4>
                    <Icon name="service" color="brand" darkMode={theme === 'night'} size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                    {language === 'hu' ? 'Bicikli Útlevél' : language === 'de' ? 'Fahrrad-Pass' : 'Bike Passport'}
                  </h4>
                  <div className="service-item">
                    <div className="service-info">
                      <strong>2024.11.15</strong>
                      <span className="service-time">10:30</span>
                      <p>{language === 'hu' ? 'Komplett átvizsgálás' : language === 'de' ? 'Komplette Inspektion' : 'Full service inspection'}</p>
                      <span className="service-shop">
                        <Icon name="shops" color="secondary" darkMode={theme === 'night'} size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                        {language === 'hu' ? 'Pedál Mester' : language === 'de' ? 'Radwerk Berlin' : 'Spoke & Pedal Cycles'}
                      </span>
                    </div>
                    <span className="service-cost">£45.00</span>
                  </div>
                  <div className="service-item">
                    <div className="service-info">
                      <strong>2024.08.22</strong>
                      <span className="service-time">14:00</span>
                      <p>{language === 'hu' ? 'Fékbetét csere' : language === 'de' ? 'Bremsbeläge wechseln' : 'Brake pad replacement'}</p>
                      <span className="service-shop">
                        <Icon name="shops" color="secondary" darkMode={theme === 'night'} size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                        {language === 'hu' ? 'Pedál Mester' : language === 'de' ? 'Radwerk Berlin' : 'Spoke & Pedal Cycles'}
                      </span>
                    </div>
                    <span className="service-cost">£28.00</span>
                  </div>
                </div>
              </div>
            )}

            {/* Booking Calendar - Based on BookingCalendarPage */}
            {activePreview === 'calendar' && (
              <div className="preview-calendar">
                <div className="booking-form-preview">
                  <div className="form-field">
                    <label>{language === 'hu' ? 'Válassz céget *' : language === 'de' ? 'Werkstatt wählen *' : 'Select Company *'}</label>
                    <div className="select-preview">
                      <span>{language === 'hu' ? 'Pedál Mester Kft.' : language === 'de' ? 'Radwerk Berlin GmbH' : 'Spoke & Pedal Cycles Ltd.'}</span>
                      <span className="select-arrow">▼</span>
                    </div>
                  </div>
                  <div className="form-field">
                    <label>{isShopView
                      ? (language === 'hu' ? 'Ügyfél + munkalap *' : language === 'de' ? 'Kunde + Auftrag *' : 'Customer + service ticket *')
                      : (language === 'hu' ? 'Válassz biciklit *' : language === 'de' ? 'Fahrrad wählen *' : 'Select Bike *')}
                    </label>
                    <div className="select-preview">
                      <span>{isShopView ? 'James Carter • Ticket #SRV-1042' : 'Canyon Endurace (2024) - FRB2024X8K7J'}</span>
                      <span className="select-arrow">▼</span>
                    </div>
                  </div>
                  <div className="form-field">
                    <label>{language === 'hu' ? 'Szolgáltatás típus *' : language === 'de' ? 'Service-Typ *' : 'Service Type *'}</label>
                    <div className="select-preview">
                      <span>{language === 'hu' ? 'Komplett átvizsgálás (60 perc) - £45' : language === 'de' ? 'Komplette Inspektion (60 min) - £45' : 'Full service inspection (60 min) - £45'}</span>
                      <span className="select-arrow">▼</span>
                    </div>
                  </div>
                </div>
                
                <div className="calendar-slots-preview">
                  <label>{language === 'hu' ? 'Válassz időpontot *' : language === 'de' ? 'Datum & Zeit wählen *' : 'Select Date & Time *'}</label>
                  <div className="time-slots">
                    <button type="button" className="slot-btn">09:00</button>
                    <button type="button" className="slot-btn">10:30</button>
                    <button type="button" className="slot-btn selected">14:00</button>
                    <button type="button" className="slot-btn">16:00</button>
                  </div>
                </div>

                <div className="booking-actions">
                  <button type="button" className="btn-book">{language === 'hu' ? 'Időpont foglalás' : language === 'de' ? 'Termin buchen' : 'Book Appointment'}</button>
                  <button type="button" className="btn-cancel">{language === 'hu' ? 'Mégse' : language === 'de' ? 'Abbrechen' : 'Cancel'}</button>
                </div>
              </div>
            )}

            {/* Dashboard - Based on UserDashboard */}
            {activePreview === 'dashboard' && (
              <div className="preview-dashboard">
                <div className="dash-header">
                  <h4>{isShopView ? 'Workshop Control Panel' : (language === 'hu' ? 'Üdv, Péter!' : language === 'de' ? 'Hallo, Peter!' : 'Welcome, James!')}</h4>
                  <span className="dash-subtitle">{isShopView
                    ? 'Bookings, technicians, invoices, and customer communication in one place'
                    : (language === 'hu' ? 'Bicikli szerviz központod' : language === 'de' ? 'Dein Fahrrad-Service-Center' : 'Your bike service command centre')}
                  </span>
                </div>

                {/* Stats row like real app */}
                <div className="dash-stats">
                  <div className="dash-stat">
                    <span className="dash-stat-icon">
                      <Icon name={isShopView ? 'calendar' : 'bikePassports'} color="brand" darkMode={theme === 'night'} size={24} />
                    </span>
                    <span className="dash-stat-value">{isShopView ? '23' : '3'}</span>
                    <span className="dash-stat-label">{isShopView ? 'Bookings today' : (language === 'hu' ? 'Útleveleim' : language === 'de' ? 'Pässe' : 'Passports')}</span>
                  </div>
                  <div className="dash-stat">
                    <span className="dash-stat-icon">
                      <Icon name={isShopView ? 'users' : 'service'} color="brand" darkMode={theme === 'night'} size={24} />
                    </span>
                    <span className="dash-stat-value">{isShopView ? '7' : '12'}</span>
                    <span className="dash-stat-label">{isShopView ? 'Techs on shift' : (language === 'hu' ? 'Szerviz' : language === 'de' ? 'Services' : 'Services')}</span>
                  </div>
                  <div className="dash-stat">
                    <span className="dash-stat-icon">
                      <Icon name={isShopView ? 'analytics' : 'shops'} color="brand" darkMode={theme === 'night'} size={24} />
                    </span>
                    <span className="dash-stat-value">{isShopView ? '£2,480' : '2'}</span>
                    <span className="dash-stat-label">{isShopView ? 'Revenue today' : (language === 'hu' ? 'Műhely' : language === 'de' ? 'Werkstätten' : 'Shops')}</span>
                  </div>
                </div>

                {/* Quick actions like real app */}
                <div className="dash-cta">
                  <span className="dash-cta-icon">
                    <Icon name={isShopView ? 'service' : 'bike'} color="brand" darkMode={theme === 'night'} size={24} />
                  </span>
                  <p>{isShopView ? 'Prioritize incoming service requests and assign technicians.' : (language === 'hu' ? 'Szervizre van szükséged?' : language === 'de' ? 'Service für dein Rad?' : 'Ready to service your bike?')}</p>
                  <div className="dash-cta-buttons">
                    <button type="button" className="btn-primary-small">
                      <Icon name="calendar" color="primary" darkMode={theme === 'night'} size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                      {isShopView ? 'View bookings' : (language === 'hu' ? 'Foglalás' : language === 'de' ? 'Buchen' : 'Book Now')}
                    </button>
                    <button type="button" className="btn-secondary-small">
                      <Icon name={isShopView ? 'users' : 'view'} color="secondary" darkMode={theme === 'night'} size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                      {isShopView ? 'Manage team' : (language === 'hu' ? 'Keresés' : language === 'de' ? 'Suchen' : 'Find Shop')}
                    </button>
                  </div>
                </div>

                {/* My Bike Passports list like real app */}
                <div className="dash-bikes">
                  <h5>
                    <Icon name={isShopView ? 'service' : 'bikePassports'} color="brand" darkMode={theme === 'night'} size={18} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                    {isShopView ? 'Open service queue' : (language === 'hu' ? 'Kerékpár Útleveleim' : language === 'de' ? 'Meine Fahrradpässe' : 'My Bike Passports')}
                  </h5>
                  <div className="dash-bike-item">
                    <span className="bike-name">{isShopView ? 'Ticket #SRV-1042 • Brake service' : 'Canyon Endurace CF SL'}</span>
                    <span className="bike-meta">{isShopView ? 'Assigned to Alex • 10:30' : `2024 • 12 ${language === 'hu' ? 'szerviz' : 'services'}`}</span>
                  </div>
                  <div className="dash-bike-item">
                    <span className="bike-name">{isShopView ? 'Ticket #SRV-1044 • Fork setup' : 'Specialized Rockhopper'}</span>
                    <span className="bike-meta">{isShopView ? 'Awaiting parts • 14:00' : `2022 • 5 ${language === 'hu' ? 'szerviz' : 'services'}`}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Intro Video by Denes */}
      <section className="intro-video-section reveal-on-scroll">
        <div className="section-header">
          <span className="eyebrow">{copy.introVideo.eyebrow}</span>
          <h2>{copy.introVideo.title}</h2>
          <p className="intro-video-subtitle">{copy.introVideo.subtitle}</p>
        </div>
        <div className="intro-video-wrapper">
          <div className="intro-video-container">
            <iframe
              width="560"
              height="315"
              src="https://www.youtube.com/embed/42dB2M9uQJg?si=IgxqBL0AwwYK4E4A"
              title="Frame-Book introduction by Denes"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section reveal-on-scroll">
        {roleStats.map((item) => (
          <div className="stat-item" key={item.label}>
            <span className="stat-number">{item.value}</span>
            <span className="stat-label">{item.label}</span>
          </div>
        ))}
      </section>

      {/* How It Works */}
      <section className="how-it-works-section">
        <div className="section-header reveal-on-scroll">
          <span className="eyebrow">{copy.howItWorks.eyebrow}</span>
          <h2>{activeHowItWorksTitle}</h2>
        </div>

        <div className="steps-grid">
          {activeHowItWorks.map((step, index) => (
            <div key={index} className="step-card reveal-on-scroll" style={{ transitionDelay: `${index * 100}ms` }}>
              <span className="step-number">{step.number}</span>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section" ref={featuresRef}>
        <div className="section-header reveal-on-scroll">
          <span className="eyebrow">{copy.features.eyebrow}</span>
          <h2>{isShopView ? 'Complete Workshop Feature Catalog' : copy.features.title}</h2>
        </div>

        {isShopView ? (
          <div className="shop-feature-catalog-grid">
            {shopFeatureColumns.map((column, columnIndex) => (
              <div key={`column-${columnIndex}`} className="shop-feature-column">
                {column.map((feature, index) => {
                  const isExpanded = expandedShopFeatureId === feature.id;
                  const bodyHeight = shopFeatureBodyRefs.current[feature.id]?.scrollHeight || 0;
                  return (
                    <div
                      key={feature.id}
                      className="shop-feature-item"
                      style={{ transitionDelay: `${Math.min(index + (columnIndex * 5), 10) * 60}ms` }}
                    >
                      <button
                        type="button"
                        className={`shop-feature-trigger ${isExpanded ? 'active' : ''}`}
                        onClick={() => setExpandedShopFeatureId((prev) => (prev === feature.id ? null : feature.id))}
                        aria-expanded={isExpanded}
                        aria-controls={`shop-feature-panel-${feature.id}`}
                      >
                        <span>{feature.name}</span>
                        <span className="shop-feature-caret">{isExpanded ? '−' : '+'}</span>
                      </button>
                      <div
                        id={`shop-feature-panel-${feature.id}`}
                        className={`shop-feature-panel ${isExpanded ? 'open' : ''}`}
                        style={{ maxHeight: isExpanded ? `${bodyHeight}px` : '0px' }}
                      >
                        <p
                          ref={(el) => {
                            shopFeatureBodyRefs.current[feature.id] = el;
                          }}
                          className="shop-feature-description"
                        >
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        ) : (
          <>
            <article className="bike-passport-highlight reveal-on-scroll">
              <div className="bike-passport-media">
                <img src={bikePassportImage} alt="Bike Passport screenshot from the app" loading="lazy" />
              </div>
              <div className="bike-passport-content">
                <h3>Bike Passport: one place for your bike&apos;s full story</h3>
                <p>
                  The Bike Passport helps you prove condition, service quality, and ownership history.
                  It turns random service notes into a clean timeline you can trust.
                </p>
                <ul className="bike-passport-points">
                  {cyclistPassportHighlights.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              </div>
            </article>

            <div className="features-grid">
              {visibleFeatureGroups.map((feature, index) => (
                <div
                  key={index}
                  className="feature-card reveal-on-scroll"
                  style={{ transitionDelay: `${index * 80}ms` }}
                >
                  <span className="feature-icon">
                    <Icon name={feature.iconName || getIconNameFromEmoji(feature.icon)} color="brand" darkMode={theme === 'night'} size={32} />
                  </span>
                  <h3>{feature.title}</h3>
                  <p className="feature-subtitle">{feature.subtitle}</p>
                  <ul>
                    {feature.bullets.map((bullet, i) => (
                      <li key={i}>{bullet}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      {/* Testimonials */}
      <section className="testimonials-section">
        <div className="section-header reveal-on-scroll">
          <span className="eyebrow">{copy.testimonials.eyebrow}</span>
          <h2>{copy.testimonials.title}</h2>
        </div>

        <div className="testimonials-carousel reveal-on-scroll">
          <div className="testimonial-card" key={activeTestimonial}>
            <blockquote>"{copy.testimonials.items[activeTestimonial].quote}"</blockquote>
            <div className="testimonial-author">
              <div className="author-avatar">
                {copy.testimonials.items[activeTestimonial].author[0]}
              </div>
              <div>
                <strong>{copy.testimonials.items[activeTestimonial].author}</strong>
                <span>{copy.testimonials.items[activeTestimonial].role}</span>
              </div>
            </div>
          </div>
          <div className="testimonial-dots">
            {copy.testimonials.items.map((_, index) => (
              <button 
                key={index}
                type="button"
                className={index === activeTestimonial ? 'active' : ''}
                onClick={() => setActiveTestimonial(index)}
                aria-label={`Testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section" ref={pricingRef}>
        <div className="section-header reveal-on-scroll">
          {!isCyclistView && (
            <>
              <span className="eyebrow">{copy.pricing.eyebrow}</span>
              <h2>{copy.pricing.title}</h2>
              <p>{copy.pricing.description}</p>
            </>
          )}
        </div>

        {/* FREE - Cyclist Plan - Separated Section */}
        {!isShopView && (
        <div className="pricing-free-section reveal-on-scroll">
          <div className="pricing-free-header">
            <Icon name="bike" color="brand" darkMode={theme === 'night'} size={28} />
            <h3>{language === 'hu' ? 'Kerékpárosoknak' : language === 'de' ? 'Für Radfahrer' : 'For Cyclists'}</h3>
          </div>
          {pricingPlans.filter(p => p.tier === 'free').map((plan, index) => (
            <div key={index} className="pricing-card-free">
              <div className="free-card-content">
                <div className="free-card-left">
                  <h4>{plan.name}</h4>
                  <p className="plan-audience">{plan.audience}</p>
                  <div className="plan-price">
                    <span className="price free-price">{plan.price}</span>
                    <span className="period">{plan.period}</span>
                  </div>
                </div>
                <div className="free-card-benefits">
                  <ul>
                    {plan.benefits.slice(0, 5).map((benefit, i) => (
                      <li key={i}>
                        <span className="check">
                          <Icon name="success" color="brand" darkMode={theme === 'night'} size={14} />
                        </span>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="free-card-actions">
                  <button 
                    type="button" 
                    className="btn-more-info"
                    onClick={() => setDetailsModal({ open: true, tier: 'free' })}
                  >
                    {language === 'hu' ? 'További infó' : language === 'de' ? 'Mehr Info' : 'More info'}
                  </button>
                  <Link to={plan.ctaLink} className="btn-primary">
                    {plan.cta}
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
        )}

        {/* PAID - Shop Plans */}
        {!isCyclistView && (
        <div className="pricing-paid-section reveal-on-scroll">
          <div className="pricing-paid-header">
            <Icon name="shop" color="brand" darkMode={theme === 'night'} size={28} />
            <h3>{language === 'hu' ? 'Szervizeknek' : language === 'de' ? 'Für Werkstätten' : 'For Workshops'}</h3>
          </div>

          <div className="pricing-grid">
            {pricingPlans.filter(p => ['basic', 'professional', 'enterprise'].includes(p.tier)).map((plan, index) => (
              <div 
                key={index}
                className={`pricing-card ${plan.highlighted ? 'highlighted' : ''}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                {plan.badge && <span className="plan-badge">{plan.badge}</span>}
                <h3>{plan.name}</h3>
                <p className="plan-audience">{plan.audience}</p>
                <div className="plan-price">
                  {plan.originalPrice && <span className="price-original">{plan.originalPrice}</span>}
                  <span className="price">{plan.price}</span>
                  <span className="period">{plan.period}</span>
                </div>
                {plan.campaignText && <p className="plan-campaign-note">{plan.campaignText}</p>}
                <ul>
                  {plan.benefits.map((benefit, i) => (
                    <li
                      key={i}
                      style={isSmartEnterprisePoint(benefit)
                        ? {
                            background: theme === 'night' ? 'rgba(74, 140, 24, 0.16)' : 'rgba(61, 90, 32, 0.12)',
                            border: theme === 'night' ? '1px solid rgba(217, 249, 157, 0.28)' : '1px solid rgba(61, 90, 32, 0.35)',
                            borderRadius: '8px',
                            padding: '8px 10px'
                          }
                        : undefined
                      }
                    >
                      <span className="check">
                        <Icon name="success" color="brand" darkMode={theme === 'night'} size={16} />
                      </span>
                      <span style={{ fontWeight: isSmartEnterprisePoint(benefit) ? '700' : '500' }}>{benefit}</span>
                      {isSmartEnterprisePoint(benefit) && (
                        <span
                          style={{
                            marginLeft: 'auto',
                            fontSize: '10px',
                            fontWeight: '700',
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            color: theme === 'night' ? '#d9f99d' : '#3d5a20'
                          }}
                        >
                          Key
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
                <div className="pricing-card-actions">
                  <button 
                    type="button" 
                    className="btn-more-info"
                    onClick={() => setDetailsModal({ open: true, tier: plan.tier })}
                  >
                    {language === 'hu' ? 'További infó' : language === 'de' ? 'Mehr Info' : 'More info'}
                  </button>
                  <Link 
                    to={plan.ctaLink} 
                    className={`btn-primary btn-full ${plan.highlighted ? '' : 'outline'}`}
                  >
                    {plan.cta}
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* Lifetime - Subtle one-liner */}
          <div className="lifetime-banner reveal-on-scroll">
            <div className="lifetime-content">
              <span className="lifetime-icon">💎</span>
              <span className="lifetime-text">
                <strong>{language === 'hu' ? 'Lifetime csomag' : language === 'de' ? 'Lifetime-Paket' : 'Lifetime Plan'}</strong>
                {' — '}
                {language === 'hu' ? 'Enterprise funkciók örökre, egyszeri £10,000 fizetéssel.' : 
                 language === 'de' ? 'Enterprise-Funktionen für immer, einmalig £10.000.' : 
                 'Enterprise features forever with one-time £10,000 payment.'}
              </span>
              <a 
                href="mailto:sales@frame-book.com?subject=Lifetime%20Plan%20Inquiry" 
                className="lifetime-link"
              >
                {language === 'hu' ? 'Kapcsolatfelvétel' : language === 'de' ? 'Kontaktieren' : 'Contact us'}
              </a>
            </div>
          </div>
        </div>
        )}

        {/* Try Demo Button */}
        {!user && isShopView && (
          <div className="try-demo-container reveal-on-scroll">
            <button 
              type="button"
              className="btn-try-demo"
              onClick={handleTryDemo}
              disabled={demoLoading}
            >
              {demoLoading ? '...' : 'Try Demo'}
            </button>
            <p className="try-demo-hint">{language === 'hu' ? 'Regisztráció nélkül' : language === 'de' ? 'Ohne Registrierung' : 'No registration required'}</p>
          </div>
        )}
      </section>

      {/* Tier Details Modal */}
      {detailsModal.open && detailsModal.tier && tierDetails[language]?.[detailsModal.tier] && (
        <div className="tier-details-modal-overlay" onClick={() => setDetailsModal({ open: false, tier: null })}>
          <div className="tier-details-modal" onClick={(e) => e.stopPropagation()}>
            <button 
              type="button" 
              className="modal-close" 
              onClick={() => setDetailsModal({ open: false, tier: null })}
              aria-label="Close"
            >
              ×
            </button>
            <div className="modal-header">
              <h2>{tierDetails[language][detailsModal.tier].title}</h2>
              <p className="modal-subtitle">{tierDetails[language][detailsModal.tier].subtitle}</p>
            </div>
            <div className="modal-body">
              <p className="modal-description">{tierDetails[language][detailsModal.tier].description}</p>
              <h4>{language === 'hu' ? 'Tartalmazza:' : language === 'de' ? 'Enthält:' : 'Includes:'}</h4>
              <ul className="feature-list-detailed">
                {tierDetails[language][detailsModal.tier].features.map((feature, i) => (
                  <li
                    key={i}
                    style={(feature.highlight || isSmartEnterprisePoint(feature.name))
                      ? {
                          background: theme === 'night' ? 'rgba(74, 140, 24, 0.16)' : 'rgba(61, 90, 32, 0.12)',
                          border: theme === 'night' ? '1px solid rgba(217, 249, 157, 0.28)' : '1px solid rgba(61, 90, 32, 0.35)',
                          borderRadius: '10px',
                          padding: '10px 12px'
                        }
                      : undefined
                    }
                  >
                    <span className="feature-check">
                      <Icon name="success" color="brand" darkMode={theme === 'night'} size={18} />
                    </span>
                    <div>
                      <strong style={{ fontWeight: (feature.highlight || isSmartEnterprisePoint(feature.name)) ? '700' : '600' }}>
                        {feature.name}
                      </strong>
                      <p>{feature.description}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="modal-footer">
              {detailsModal.tier === 'free' ? (
                <Link to="/onboarding" className="btn-primary btn-large" onClick={() => setDetailsModal({ open: false, tier: null })}>
                  {language === 'hu' ? 'Regisztrálj ingyen' : language === 'de' ? 'Kostenlos registrieren' : 'Register for free'}
                </Link>
              ) : (
                <Link to={`/onboarding?plan=${detailsModal.tier}`} className="btn-primary btn-large" onClick={() => setDetailsModal({ open: false, tier: null })}>
                  {language === 'hu' ? 'Próba indítása' : language === 'de' ? 'Testphase starten' : 'Start trial'}
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="site-footer">
        <div className="footer-grid">
          <div className="footer-brand">
            <div className="footer-logo">
              {logoUrl && (
                <img src={logoUrl} alt="Frame-Book Logo" className="footer-logo-img" />
              )}
            </div>
            <p>{copy.footer.tagline}</p>
          </div>
          <div className="footer-links">
            <span className="footer-label">{copy.footer.contact}</span>
            <a href="tel:+441392343922">44 1392 343922</a>
            <span style={{ fontSize: '11px', opacity: 0.65 }}>Call anytime between 7am and 9pm</span>
            <a href="mailto:support@frame-book.com">support@frame-book.com</a>
          </div>
          <div className="footer-links">
            <span className="footer-label">{copy.footer.legal}</span>
            <Link to="/privacy">{copy.footer.privacy}</Link>
            <Link to="/terms">{copy.footer.terms}</Link>
          </div>
        </div>
        <div className="footer-bottom">
          <span>© {new Date().getFullYear()} Frame-Book – {language === 'hu' ? 'minden jog fenntartva' : language === 'de' ? 'alle Rechte vorbehalten' : 'all rights reserved'}.</span>
          <a href="https://webapptown.com" target="_blank" rel="noreferrer">
            By WebAppTown
          </a>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
